import { InformationCircleIcon, ChartBarIcon } from "@heroicons/react/outline";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import { Toast, ToastType } from "./components/alerts/Toast";
import { Grid } from "./components/grid/Grid";
import { Keyboard } from "./components/keyboard/Keyboard";
import { AboutModal } from "./components/modals/AboutModal";
import { InfoModal } from "./components/modals/InfoModal";
import { WinModal } from "./components/modals/WinModal";
import { GameOverModal } from "./components/modals/GameOverModal";
import { StatsModal } from "./components/modals/StatsModal";
import { ThemeToggle } from "./components/ThemeToggle";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { SeoHead } from "./components/SeoHead";
import { BlogGillianJonesPage } from "./pages/BlogGillianJonesPage";
import {
  isWordInWordList,
  isWinningWord,
  getRandomSolutionWord,
} from "./lib/words";
import { getStats, updateStats } from "./lib/stats";
import { shareResult } from "./lib/share";
import { handleKeyDown } from "./lib/keyboard";
import type { GameLocale } from "./i18n/types";
import { getMessages } from "./i18n/messages";

function WordleGame() {
  const location = useLocation();
  const locale: GameLocale = location.pathname.startsWith("/no") ? "no" : "da";
  const m = getMessages(locale);

  const solution = useMemo(() => getRandomSolutionWord(locale), [locale]);

  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [isGameWon, setIsGameWon] = useState(false);
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [gameStats, setGameStats] = useState(() => getStats(locale));
  const [isGameOverModalOpen, setIsGameOverModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    type: ToastType;
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    setGuesses([]);
    setCurrentGuess("");
    setIsGameWon(false);
    setIsWinModalOpen(false);
    setIsGameOverModalOpen(false);
    setGameStats(getStats(locale));
  }, [locale]);

  useEffect(() => {
    const key =
      locale === "no" ? "wordle-no-seen-info" : "wørdle-seen-info";
    if (!localStorage.getItem(key)) {
      setIsInfoModalOpen(true);
    } else {
      setIsInfoModalOpen(false);
    }
  }, [locale]);

  useEffect(() => {
    if (isGameWon) {
      setIsWinModalOpen(true);
    }
  }, [isGameWon]);

  const onChar = useCallback(
    (value: string) => {
      if (currentGuess.length < 5 && guesses.length < 6) {
        setCurrentGuess(`${currentGuess}${value}`);
      }
    },
    [currentGuess, guesses.length]
  );

  const onDelete = useCallback(() => {
    setCurrentGuess(currentGuess.slice(0, -1));
  }, [currentGuess]);

  const onEnter = useCallback(() => {
    if (currentGuess.length < 5) {
      setToast({
        isOpen: true,
        message: m.notEnoughLetters,
        type: "error",
      });
      setTimeout(() => setToast((prev) => ({ ...prev, isOpen: false })), 2000);
      return;
    }

    if (!isWordInWordList(currentGuess, locale)) {
      setToast({
        isOpen: true,
        message: m.wordNotFound,
        type: "error",
      });
      setTimeout(() => setToast((prev) => ({ ...prev, isOpen: false })), 2000);
      return;
    }

    const winningWord = isWinningWord(currentGuess, solution);

    if (currentGuess.length === 5 && guesses.length < 6 && !isGameWon) {
      setGuesses((g) => [...g, currentGuess]);
      setCurrentGuess("");

      if (winningWord) {
        const newStats = updateStats(true, locale);
        setGameStats(newStats);
        return setIsGameWon(true);
      }

      if (guesses.length === 5) {
        const newStats = updateStats(false, locale);
        setGameStats(newStats);
        setIsGameOverModalOpen(true);
        return;
      }
    }
  }, [
    currentGuess,
    guesses.length,
    isGameWon,
    locale,
    m.notEnoughLetters,
    m.wordNotFound,
    solution,
  ]);

  useEffect(() => {
    const handleKeyDownEvent = (event: KeyboardEvent) => {
      handleKeyDown(event, onChar, onDelete, onEnter);
    };

    document.addEventListener("keydown", handleKeyDownEvent);
    return () => document.removeEventListener("keydown", handleKeyDownEvent);
  }, [onChar, onDelete, onEnter]);

  const closeInfo = () => {
    setIsInfoModalOpen(false);
    const key =
      locale === "no" ? "wordle-no-seen-info" : "wørdle-seen-info";
    localStorage.setItem(key, "1");
  };

  return (
    <>
      <SeoHead locale={locale} />
      <div className="game-container">
        <header className="game-header">
          <h1>{m.gameTitle}</h1>
          <div className="header-icons-right">
            <LanguageSwitcher locale={locale} />
            <ThemeToggle locale={locale} />
            <button
              onClick={() => setIsStatsModalOpen(true)}
              className="header-icon-btn"
            >
              <ChartBarIcon className="h-6 w-6" />
            </button>
            <button
              onClick={() => setIsInfoModalOpen(true)}
              className="header-icon-btn"
            >
              <InformationCircleIcon className="h-6 w-6" />
            </button>
          </div>
        </header>

        <div className="adsense-container">
          <ins
            className="adsbygoogle"
            style={{ display: "inline-block", width: "320px", height: "50px" }}
            data-ad-client="ca-pub-2451827670712916"
            data-ad-slot="5295346592"
          ></ins>
        </div>

        <div className="grid-wrapper">
          <Grid
            guesses={guesses}
            currentGuess={currentGuess}
            solution={solution}
          />
        </div>

        <Keyboard
          onChar={onChar}
          onDelete={onDelete}
          onEnter={onEnter}
          guesses={guesses}
          solution={solution}
          enterLabel={m.keyboardEnter}
          deleteLabel={m.keyboardDelete}
          ariaLabel={m.keyboardAria}
        />

        <WinModal
          isOpen={isWinModalOpen}
          handleClose={() => setIsWinModalOpen(false)}
          guesses={guesses}
          solution={solution}
          onShare={() => shareResult(guesses, true, solution, locale)}
          locale={locale}
        />
        <InfoModal
          isOpen={isInfoModalOpen}
          handleClose={closeInfo}
          locale={locale}
        />
        <AboutModal
          isOpen={isAboutModalOpen}
          handleClose={() => setIsAboutModalOpen(false)}
        />
        <StatsModal
          isOpen={isStatsModalOpen}
          handleClose={() => setIsStatsModalOpen(false)}
          gameStats={gameStats}
          locale={locale}
        />
        <GameOverModal
          isOpen={isGameOverModalOpen}
          handleClose={() => setIsGameOverModalOpen(false)}
          guesses={guesses}
          solution={solution}
          onShare={() => shareResult(guesses, false, solution, locale)}
          locale={locale}
        />
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((prev) => ({ ...prev, isOpen: false }))}
        />
      </div>
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<WordleGame />} />
      <Route path="/no" element={<WordleGame />} />
      <Route
        path="/blog/makeupspejle-gillian-jones"
        element={<BlogGillianJonesPage />}
      />
    </Routes>
  );
}
